<script setup lang="ts">
import { computed } from "vue";

import actions from "~/actions";
import DropdownItemWrapper from "~/components/dumb/DropdownItemWrapper.vue";
import OptionComponent from "~/components/options/Option.vue";
import OptionEditDropdown from "~/components/options/OptionEditDropdown.vue";
import { colorsByTheme } from "~/constants/style";
import { DotsHorizontalIcon, XIcon } from "~/icons";
import { CommandId } from "~/shared/enums";
import type { MultiselectDropdownMenuItem, Option } from "~/shared/types";
import { useAppStore, usePageStore, useTenantStore } from "~/stores";
import { getChipColorsHex, withCssAlpha } from "~/utils/color";

const LIGHT_HOVER_COLOR = withCssAlpha(colorsByTheme.light.bgStd, 0.3);

const props = defineProps<{
  option: Option;
  selected: boolean;
  isChip?: boolean;
  isFilter?: boolean;
  replaceEnabled?: boolean;
}>();

const appStore = useAppStore();
const pageStore = usePageStore();
const tenantStore = useTenantStore();

const emit = defineEmits<{
  remove: [];
  swap: [duid: string];
  select: [item: MultiselectDropdownMenuItem];
}>();

const colorSet = computed(() => getChipColorsHex(props.option.colorHex, pageStore.theme));
const hoverColor = computed(() => (pageStore.theme === "dark" ? colorSet.value.back : LIGHT_HOVER_COLOR));

const onContextMenu = (event: MouseEvent) => {
  if ((tenantStore.isDart && !pageStore.adminHidden && event.altKey) || pageStore.isPublicForm) {
    return;
  }

  appStore.openContextMenu(event as PointerEvent, actions.context.option(props.option), { maxHeightPixels: 566 });
};

const onOptionClick = (e: Event) => {
  e.stopImmediatePropagation();
  emit("select", {
    value: props.option.duid,
    label: props.option.title,
    selected: props.selected,
    component: OptionComponent,
  });
};
</script>

<template>
  <div
    v-if="selected && isChip"
    class="group/option relative"
    :style="{ '--background': hoverColor }"
    @contextmenu="onContextMenu">
    <OptionComponent :option="option" item-class="min-w-[40px]" show-ancestors>
      <div
        class="cursor-pointer rounded-sm hover:bg-[var(--background)]"
        :style="{ color: colorSet.fore }"
        @click="emit('remove')"
        @keydown.enter="emit('remove')">
        <XIcon class="icon-xs" />
      </div>
    </OptionComponent>
    <div
      class="absolute inset-y-px right-[19px] bg-lt opacity-0"
      :class="!pageStore.isPublicForm && 'group-hover/option:opacity-100'">
      <div
        v-if="!pageStore.isPublicForm && !isFilter"
        class="flex size-full items-center py-0.5 pr-0.5 pl-1"
        :style="{ color: colorSet.fore, backgroundColor: colorSet.back }">
        <OptionEditDropdown :option="option">
          <div class="cursor-pointer rounded-sm hover:bg-[var(--background)]">
            <DotsHorizontalIcon class="icon-xs cursor-pointer rounded-sm" />
          </div>
        </OptionEditDropdown>
      </div>
    </div>
  </div>

  <div v-else class="w-full" @contextmenu="onContextMenu" @click.stop="onOptionClick" @keydown.enter="onOptionClick">
    <DropdownItemWrapper
      :selected="selected"
      :replace-enabled="isFilter && replaceEnabled"
      :command-id="CommandId.SWAP"
      class="gap-2"
      @replace="emit('swap', option.duid)">
      <div class="flex-1 truncate">
        <OptionComponent :option="option" show-ancestors />
      </div>
      <template v-if="!isFilter && !pageStore.isMobile && !pageStore.isPublicForm" #actions>
        <OptionEditDropdown :option="option" class="opacity-0 [[data-focused]_&]:opacity-100">
          <DotsHorizontalIcon class="icon-sm cursor-pointer rounded-sm text-lt hover:bg-hvy focus:outline-hidden" />
        </OptionEditDropdown>
      </template>
    </DropdownItemWrapper>
  </div>
</template>
